import React from "react";
import { fmtUSDStrict, fmtCcy } from "@/lib/format";
import { SwipeConfirm } from "@/components/SwipeConfirm";
import { Button } from "@/components/ui/button";
import { ArrowLeft, AlertTriangle, Info, Repeat } from "lucide-react";

const num = (n, d = 4) => Number(n).toLocaleString("en-US", { maximumFractionDigits: d });

/** The last screen before money moves: what you are doing, in a sentence, then
 *  every cost line the backend computed — never re-calculated here. */
export const OrderPreview = ({ preview, onBack, onConfirm, submitting }) => (
  <div className="space-y-4" data-testid="order-preview">
    <p className="text-sm leading-relaxed font-medium" data-testid="order-preview-sentence">
      {preview.sentence}
    </p>

    <div className="rounded-xl border divide-y" data-testid="order-preview-lines">
      {preview.lines.map((l, i) => (
        <div key={l.label} className={`flex items-center justify-between gap-3 px-3.5 py-2.5 text-sm ${l.emphasis ? "bg-secondary font-semibold" : ""}`}
          data-testid={`order-preview-line-${i}`}>
          <span className={l.emphasis ? "" : "text-muted-foreground"}>
            {l.label}
            {l.recurring && <Repeat className="inline h-3 w-3 ms-1.5 -mt-0.5 text-muted-foreground" />}
          </span>
          <span className="numeric">{l.currency === "USD" ? fmtUSDStrict(l.value) : fmtCcy(l.value, l.currency)}</span>
        </div>
      ))}
    </div>

    {preview.notes.map((n, i) => (
      <div key={n} className="flex items-start gap-2 text-xs text-muted-foreground leading-relaxed"
        data-testid={`order-preview-note-${i}`}>
        <Info className="h-3.5 w-3.5 mt-px shrink-0" /><span>{n}</span>
      </div>
    ))}

    {preview.blockers.map((b, i) => (
      <div key={b} className="flex items-start gap-2 rounded-lg bg-destructive/10 p-2.5 text-xs font-medium text-destructive leading-relaxed"
        data-testid={`order-preview-blocker-${i}`}>
        <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" /><span>{b}</span>
      </div>
    ))}

    {preview.contract && (
      <p className="text-xs text-muted-foreground numeric" data-testid="order-preview-lots">
        {num(preview.qty)} {preview.unit_label} = {num(preview.lots)} {preview.contract.exchange} lot
        {Math.abs(preview.lots - 1) < 1e-9 ? "" : "s"} ({num(preview.contract.lot_size, 2)} {preview.contract.lot_unit} each)
      </p>
    )}

    <div className="flex gap-2">
      <Button variant="outline" onClick={onBack} disabled={submitting} data-testid="order-preview-back">
        <ArrowLeft className="h-4 w-4 me-1.5" />Back
      </Button>
      <div className="flex-1">
        <SwipeConfirm
          label={preview.order_type === "trailing_stop"
            ? `Swipe to arm ${preview.trail_percent}% trailing stop`
            : preview.order_type === "stop"
            ? "Swipe to arm stop loss"
            : preview.resting
            ? `Swipe to place limit ${preview.side}`
            : `Swipe to ${preview.side} · ${fmtUSDStrict(preview.total_usd)}`}
          tone={preview.side === "buy" ? "buy" : "sell"}
          disabled={!preview.can_submit}
          submitting={submitting}
          faceIdPayload={{ symbol: preview.symbol, side: preview.side, qty: preview.qty }}
          onConfirm={onConfirm} />
      </div>
    </div>
  </div>
);
